import { useEffect, useMemo, useRef, useState } from 'react'
import maplibregl from 'maplibre-gl'
import 'maplibre-gl/dist/maplibre-gl.css'
import { useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { X, MapPin, Radio } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { useHorizontalScroll } from '@/hooks/useHorizontalScroll'
import SearchBar from '@/components/ui/SearchBar'
import { CATS } from '@/data/mockData'

const C = {
  teal: '#0F5C5C',
  terra: '#D9785B',
  ink: '#202523',
  ink2: '#6D7471',
  border: '#E1E5E3',
  live: '#D85F5F',
  grayBg: '#F1F3F2',
}
const FH = "'Plus Jakarta Sans', sans-serif"
const FB = "'Inter', sans-serif"

// Centro de Pereira, Risaralda
const PEREIRA_CENTER: [number, number] = [-75.6906, 4.8087]
// Estilo vectorial 100% gratuito, sin API key (OpenFreeMap)
const MAP_STYLE = 'https://tiles.openfreemap.org/styles/liberty'

const CAT_EMOJI: Record<string, string> = Object.fromEntries(CATS.map((c) => [c.k, c.emoji]))

interface BizRow {
  id: string
  name: string
  zone: string
  latitude: number | null
  longitude: number | null
}

interface PostRow {
  id: string
  business_id: string
  type: string
  title: string
  category: string | null
}

export default function MapPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const mapContainer = useRef<HTMLDivElement>(null)
  const mapRef = useRef<maplibregl.Map | null>(null)
  const markersRef = useRef<maplibregl.Marker[]>([])
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [mapReady, setMapReady] = useState(false)
  const [catFilters, setCatFilters] = useState<Set<string>>(new Set())
  const filtersScrollRef = useHorizontalScroll<HTMLDivElement>()

  const toggleFilter = (k: string) =>
    setCatFilters((prev) => {
      const next = new Set(prev)
      if (next.has(k)) {
        next.delete(k)
      } else {
        next.add(k)
      }
      return next
    })

  // Negocios con coordenadas + publicaciones activas (refresca con cada cambio en tiempo real)
  const { data } = useQuery({
    queryKey: ['map-data'],
    queryFn: async () => {
      if (!supabase) return { businesses: [] as BizRow[], posts: [] as PostRow[] }
      const [{ data: businesses }, { data: posts }] = await Promise.all([
        supabase
          .from('businesses')
          .select('id, name, zone, latitude, longitude')
          .not('latitude', 'is', null),
        supabase
          .from('posts')
          .select('id, business_id, type, title, category')
          .eq('is_active', true)
          .eq('is_permanent', false)
          .or(`ends_at.is.null,ends_at.gt.${new Date().toISOString()}`),
      ])
      return {
        businesses: (businesses ?? []) as BizRow[],
        posts: (posts ?? []) as PostRow[],
      }
    },
    staleTime: 0,
  })

  // Tiempo real: cualquier cambio en publicaciones actualiza el mapa al instante
  useEffect(() => {
    if (!supabase) return
    const channel = supabase
      .channel('map-posts-realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'posts' }, () => {
        queryClient.invalidateQueries({ queryKey: ['map-data'] })
      })
      .subscribe()
    return () => {
      supabase?.removeChannel(channel)
    }
  }, [queryClient])

  const postsByBiz = useMemo(() => {
    const map = new Map<string, PostRow[]>()
    for (const p of data?.posts ?? []) {
      const list = map.get(p.business_id) ?? []
      list.push(p)
      map.set(p.business_id, list)
    }
    return map
  }, [data])

  // Inicializar el mapa una sola vez
  useEffect(() => {
    if (!mapContainer.current || mapRef.current) return
    const map = new maplibregl.Map({
      container: mapContainer.current,
      style: MAP_STYLE,
      center: PEREIRA_CENTER,
      zoom: 13.2,
      attributionControl: false,
    })
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right')
    map.on('load', () => setMapReady(true))
    mapRef.current = map
    return () => {
      map.remove()
      mapRef.current = null
    }
  }, [])

  // Pintar/actualizar marcadores cuando llegan o cambian los datos
  useEffect(() => {
    const map = mapRef.current
    if (!map || !mapReady || !data) return

    markersRef.current.forEach((m) => m.remove())
    markersRef.current = []

    for (const biz of data.businesses) {
      if (biz.latitude == null || biz.longitude == null) continue
      const allPosts = postsByBiz.get(biz.id) ?? []
      const activePosts =
        catFilters.size === 0 ? allPosts : allPosts.filter((p) => catFilters.has(p.category ?? ''))

      // Con filtros activos, los negocios sin ninguna actividad de esas categorías se ocultan
      if (catFilters.size > 0 && activePosts.length === 0) continue

      const hasLive = activePosts.some((p) => p.type === 'live')
      const hasActivity = activePosts.length > 0
      const emoji = CAT_EMOJI[activePosts[0]?.category ?? ''] ?? '📍'

      // El elemento raíz NO debe tocarse con `transform` — MapLibre lo usa
      // internamente para posicionar el pin sobre el mapa. El efecto hover
      // (escala) se aplica a un <span> interno para no pisar esa posición.
      const el = document.createElement('button')
      el.setAttribute('aria-label', biz.name)
      el.style.cssText = `
        width: 42px; height: 42px; border: none; background: transparent;
        cursor: pointer; padding: 0;
      `

      const inner = document.createElement('span')
      inner.style.cssText = `
        width: 100%; height: 100%; border-radius: 50% 50% 50% 6px;
        background: ${hasLive ? C.live : hasActivity ? C.teal : '#fff'};
        border: 2.5px solid #fff;
        box-shadow: 0 4px 12px rgba(0,0,0,.28);
        display: flex; align-items: center; justify-content: center;
        font-size: 19px; transition: transform .15s ease; transform-origin: bottom center;
      `
      if (hasLive) {
        inner.style.animation = 'pxPulseMarker 1.6s infinite'
      }
      inner.textContent = emoji
      el.appendChild(inner)

      el.onmouseenter = () => (inner.style.transform = 'scale(1.12)')
      el.onmouseleave = () => (inner.style.transform = 'scale(1)')
      el.onclick = (e) => {
        e.stopPropagation()
        setSelectedId(biz.id)
        map.flyTo({
          center: [biz.longitude as number, biz.latitude as number],
          zoom: 15,
          duration: 600,
        })
      }

      const marker = new maplibregl.Marker({ element: el, anchor: 'bottom' })
        .setLngLat([biz.longitude, biz.latitude])
        .addTo(map)
      markersRef.current.push(marker)
    }
  }, [data, postsByBiz, mapReady, catFilters])

  const selectedBiz = data?.businesses.find((b) => b.id === selectedId)
  const selectedPosts = selectedId ? (postsByBiz.get(selectedId) ?? []) : []
  const liveCount = (data?.posts ?? []).filter((p) => p.type === 'live').length

  return (
    <div className="map-shell" style={{ position: 'relative', overflow: 'hidden' }}>
      <style>{`
        @keyframes pxPulseMarker {
          0%   { box-shadow: 0 4px 12px rgba(0,0,0,.28), 0 0 0 0 rgba(216,95,95,.55); }
          70%  { box-shadow: 0 4px 12px rgba(0,0,0,.28), 0 0 0 12px rgba(216,95,95,0); }
          100% { box-shadow: 0 4px 12px rgba(0,0,0,.28), 0 0 0 0 rgba(216,95,95,0); }
        }
      `}</style>

      <div ref={mapContainer} style={{ position: 'absolute', inset: 0 }} />

      {/* Búsqueda + encabezado + filtros — flotantes sobre el mapa */}
      <div
        style={{
          position: 'absolute',
          top: 16,
          left: 16,
          right: 16,
          zIndex: 5,
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
        }}
      >
        <SearchBar style={{ maxWidth: 400, boxShadow: '0 6px 18px rgba(0,0,0,.14)' }} />

        <div
          className="map-top-row"
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'flex-start',
            gap: 10,
          }}
        >
          {/* Encabezado flotante */}
          <div
            className="map-header-card"
            style={{
              background: '#fff',
              borderRadius: 14,
              padding: '12px 16px',
              boxShadow: '0 6px 18px rgba(0,0,0,.14)',
              maxWidth: 260,
              flexShrink: 0,
            }}
          >
            <div
              style={{
                fontFamily: FH,
                fontWeight: 800,
                fontSize: 15.5,
                marginBottom: 4,
                whiteSpace: 'nowrap',
              }}
            >
              Mapa de actividades
            </div>
            <div
              style={{
                fontSize: 12.5,
                color: C.ink2,
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <Radio size={13} color={C.live} style={{ flexShrink: 0 }} />
              {liveCount} {liveCount === 1 ? 'lugar en vivo' : 'lugares en vivo'} ahora · Pereira
            </div>
          </div>

          {/* Filtros por categoría */}
          <div
            ref={filtersScrollRef}
            className="map-filters-row"
            style={{
              display: 'flex',
              gap: 8,
              overflowX: 'auto',
              scrollbarWidth: 'none',
              flex: '1 1 260px',
              minWidth: 0,
            }}
          >
            {catFilters.size > 0 && (
              <button
                onClick={() => setCatFilters(new Set())}
                style={{
                  flexShrink: 0,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  background: '#fff',
                  color: C.terra,
                  border: `1px solid ${C.terra}`,
                  borderRadius: 99,
                  padding: '8px 13px',
                  cursor: 'pointer',
                  fontFamily: FB,
                  fontWeight: 700,
                  fontSize: 13,
                  boxShadow: '0 4px 10px rgba(0,0,0,.08)',
                }}
              >
                <X size={14} />
                Limpiar filtros
              </button>
            )}
            {CATS.map((c) => {
              const active = catFilters.has(c.k)
              return (
                <button
                  key={c.k}
                  onClick={() => toggleFilter(c.k)}
                  style={{
                    flexShrink: 0,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    background: active ? C.teal : '#fff',
                    color: active ? '#fff' : C.ink,
                    border: `1px solid ${active ? C.teal : C.border}`,
                    borderRadius: 99,
                    padding: '8px 13px',
                    cursor: 'pointer',
                    fontFamily: FB,
                    fontWeight: 600,
                    fontSize: 13,
                    boxShadow: '0 4px 10px rgba(0,0,0,.08)',
                  }}
                >
                  <span>{c.emoji}</span>
                  {c.label}
                </button>
              )
            })}
          </div>
        </div>
      </div>

      {/* Leyenda — oculta cuando hay una tarjeta seleccionada para no chocar en móvil */}
      {!selectedBiz && (
        <div
          className="map-legend"
          style={{
            position: 'absolute',
            bottom: 16,
            right: 16,
            background: '#fff',
            borderRadius: 12,
            padding: '10px 14px',
            boxShadow: '0 6px 18px rgba(0,0,0,.14)',
            fontFamily: FB,
            fontSize: 12.5,
            display: 'flex',
            gap: 6,
            zIndex: 5,
          }}
        >
          <LegendDot color={C.live} label="En vivo ahora" />
          <LegendDot color={C.teal} label="Con actividad" />
          <LegendDot color="#fff" border label="Sin actividad" />
        </div>
      )}

      {/* Tarjeta del negocio seleccionado — tarjeta flotante en desktop, bottom-sheet en móvil */}
      {selectedBiz && (
        <div
          className="map-selected-card"
          style={{
            position: 'absolute',
            bottom: 16,
            left: 16,
            width: 340,
            maxWidth: 'calc(100% - 32px)',
            maxHeight: 'calc(100% - 32px)',
            display: 'flex',
            flexDirection: 'column',
            background: '#fff',
            borderRadius: 18,
            boxShadow: '0 14px 34px rgba(0,0,0,.22)',
            zIndex: 6,
          }}
        >
          {/* Handle — solo visible en móvil (ver CSS), señala que se puede interactuar como hoja */}
          <div className="map-selected-handle" />

          <div className="map-selected-header" style={{ padding: '18px 18px 0', flexShrink: 0 }}>
            <button
              onClick={() => setSelectedId(null)}
              style={{
                position: 'absolute',
                top: 10,
                right: 10,
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                padding: 4,
              }}
            >
              <X size={18} color={C.ink2} />
            </button>
            <div
              style={{
                fontFamily: FH,
                fontWeight: 800,
                fontSize: 17,
                marginBottom: 2,
                paddingRight: 20,
              }}
            >
              {selectedBiz.name}
            </div>
            <div
              style={{
                fontSize: 13,
                color: C.ink2,
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                marginBottom: 12,
              }}
            >
              <MapPin size={13} /> {selectedBiz.zone}, Pereira
            </div>
          </div>

          <div
            className="map-selected-body"
            style={{ padding: '0 18px 18px', overflowY: 'auto', minHeight: 0 }}
          >
            {selectedPosts.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {selectedPosts.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => navigate(`/post/${p.id}`)}
                    style={{
                      textAlign: 'left',
                      background: C.grayBg,
                      border: 'none',
                      borderRadius: 10,
                      padding: '9px 12px',
                      cursor: 'pointer',
                      fontFamily: FB,
                      fontSize: 13.5,
                      fontWeight: 600,
                      color: C.ink,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                    }}
                  >
                    {p.type === 'live' && (
                      <span
                        style={{
                          width: 7,
                          height: 7,
                          borderRadius: 99,
                          background: C.live,
                          flexShrink: 0,
                          animation: 'pxPulse 1.4s infinite',
                        }}
                      />
                    )}
                    {p.title}
                  </button>
                ))}
              </div>
            ) : (
              <div style={{ fontSize: 13, color: C.ink2 }}>Sin actividad activa por ahora.</div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

function LegendDot({ color, label, border }: { color: string; label: string; border?: boolean }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
      <span
        style={{
          width: 11,
          height: 11,
          borderRadius: '50% 50% 50% 4px',
          background: color,
          border: border ? `1px solid ${C.border}` : 'none',
          display: 'inline-block',
          flexShrink: 0,
        }}
      />
      {label}
    </div>
  )
}
